import { getMultiCartItems } from 'api/cart/getMultiCartItems'
import { getMultiCartSummaries } from 'api/cart/getMultiCartSummaries'
import { getMultiCartMembers } from 'api/cart/getMultiCartMembers'
import { assign, DoneActorEvent, ErrorActorEvent, fromPromise, log, setup } from 'xstate'

type MultiCartMachineInput = {
  memberId?: string
}

type MultiCartMachineContext = {
  cartSummary: MachineContext.CartSummary | { status: 'error', error: string } | { status: 'loading' }
  cartItems: MachineContext.CartItems | { status: 'error', error: string } | { status: 'loading' }
  cartMembers: MachineContext.CartMembers | { status: 'error', error: string } | { status: 'loading' }
  activeCartId?: string
  // Redux state from input
  memberId?: string
}

type MultiCartMachineEvents = {
  type: 'set active cart'
  cartId: string
} | {
  type: 'successful login'
} | {
  type: 'failed login'
}
| DoneActorEvent<MachineContext.CartSummary, string>
| DoneActorEvent<Array<MachineContext.CartItems>, string>
| DoneActorEvent<Array<MachineContext.CartMembers>, string>
| ErrorActorEvent<MachineContext.CartSummary, string>
| ErrorActorEvent<Array<MachineContext.CartItems>, string>
| ErrorActorEvent<Array<MachineContext.CartMembers>, string>

/**
 * State machine docs - https://stately.ai/docs/state-machines-and-statecharts
 */

const multiCartMachine = setup({
  types: {
    context: {} as MultiCartMachineContext,
    events: {} as MultiCartMachineEvents,
    input: {} as MultiCartMachineInput,
  },
  actors: {
    getMultiCarts: fromPromise(getMultiCartSummaries),
    getMultiCartItems: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartItems))),
    getMultiCartMembers: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartMembers))),
  },
  actions: {
    assignCartSummary: assign({
      cartSummary: ({ event }: { event: DoneActorEvent<MachineContext.CartSummary, string> }) => ({
        ...event.output,
      })
    }),
    assignCartSummaryFetchError: assign({
      cartSummary: () => ({
        status: 'error' as const,
        error: 'Failed to load cart summary'
      })
    }),
    assignCartItems: assign({
      cartItems: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartItems>, string> }) => {
        // Merge all cart items from Promise.all results into a single object
        return event.output.reduce((acc, cartItems) => ({ ...acc, ...cartItems }), {} as MachineContext.CartItems)
      }
    }),
    assignCartItemsFetchError: assign({
      cartItems: () => ({
        status: 'error' as const,
        error: 'Failed to load cart items'
      })
    }),
    assignCartUsers: assign({
      cartMembers: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartMembers>, string> }) => {
        // Merge all cart members from Promise.all results into a single object
        return event.output.reduce((acc, cartMembers) => ({ ...acc, ...cartMembers }), {} as MachineContext.CartMembers)
      }
    }),
    assignCartUsersFetchError: assign({
      cartMembers: () => ({
        status: 'error' as const,
        error: 'Failed to load cart members'
      })
    }),
    logErrorEvent: log(({ event }: {
      event: ErrorActorEvent<MachineContext.CartSummary, string> | ErrorActorEvent<Array<MachineContext.CartItems>, string> | ErrorActorEvent<Array<MachineContext.CartMembers>, string>
    }) => `assignCartSummaryFetchError ${JSON.stringify(event.error)}`),
  },
  guards: {
    isUserLoggedIn: ({ context }) => !!context.memberId,
  },
}
).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFsCuAbALgSwMIEMAnTAWXwGMALbAOzADoqxyBrWqAZU30zAGIA2gAYAuolAAHAPaxsOKTXEgAHogBMagKz0AnJoDMARn1qALGoDsANjUAOW2oA0IAJ7r9O+ptNCh+-RammkKaagEAvuHOaFh4RKQU1HSMlMxsNJzcvIKGYkgg0rLyivmqCGqGnqa2pvq2FkI6OqaBxs5u5RZVQlYeFpqGWk1qVpHRGDgExGRUtAxMrOxcPPwCanmSMnLYCkplALTG2rY6an6awZWGBlbtiIaGNvQWNaZWhvVC5o9jIDGT8RmSQYtG2+HQ2AAXuw+BAFAxYFkGP84tNEnN6KCcOCoexhBsCltint7j0hM8hNZauYNN5NHcEI8TPQPN59MF-JT9L8UVMErNkljsDjoRl6OgpPgIOw+RxUMhkEQXLD4ZiaAA3KQsZETVH84FqsEQ0VQcWS6UZWXyxWEFwIWia8g8HY0fH4pSFba7UqIfraMIXerWHSGUy1BmaCwWXRh2wBHRCGoWOo83V8oEYoUi9hmqUy+JyhVKvhgQiEKSEegSdA8ABmFeQ9F5gPRgpoRtxYolectBetSvtGqkTuKbtEHqJLpJCBDanoYd8FzUIf0NkMDKCVnoGhMQyjIT6qdi6dbIPb2ONOe7FqgfIAImBuNh0LAVclESsm2mWwKzx2TbmN73o++DPrA7r5J6xI+oyWhbmGzRfLYxhGE4rj3Jy25aKGNhdCcQhqEeAJor+hoXp2prXvmxAPk+L6AdRmAAJK8MgsAMRkb5npq2pfseP4Glml5duajG0aB9FUb2xAsWAbEcVAg6Os6ChjgSUFTjBFiWPQlRBB4OgBD4Oi3OhM4OPOvh6NUia2FYkZEXqGZtv+V6idJmDiWBCl8rJ8lSVAJZlhWVY1pg9aEI2zYkYJ57CsJlHube8ReZJSW+ax7EBUpw4qa6ogQZsRSaaAZTadGekGE0RmNKZHSGJS2jsj43xWO8XKOSepFCRRPkpSB3kBXyACqsClllSVcWqPE6vxMWZnF2YiT2yU0QNaUrSNY2EBNK05SOLpqROxXeqVvoXLoOiBEY7w6PYXwMsYjxeOYPgGHUZw6J1AkLa5y1Af1dG7QDxCjeNClBeWlbVnWDZ8cR+q-eRAFDYDEnA4xYM7Qp+15UdkGTqdKjndoTTXY8lT3aYj3GfO1mnFY1nBKMUR-N+83JIQYBSsqsCoOQ5BwLAtYYAABBKUC0IVhInSUZ0IA4EbLl4BkNK9XSWN9HMMFzPN8LWEmQOLUiS-l6mE3LxMIFYtjkpYib6G8ARfFYFiPYzhi6IE9hWLUDQoZErM0FIEBwEo0WI3Qx1epbBwfPo9AnGc7KXCGNyPQEzwGaYV2cjYTRa5H8ypIsGTLLw0fQfL5gMrS27Z7n+g9MuX2sxHzl-sj7CVyVVtGJ4Nt3TbjtHL0DKrrYzxhBUCHeBU1iFx3ZHxb1qPEIWNodEVMfTqu0aD3ZcamKP+gbno9C+4MIQOIYgRfIvp7L0tiWbWjYE90TZR1JPbxCLfJyzlOGhDottow+ETMmAIDxdwP26otBKfU1pA0QcxTKH9Y6IFqPBKwf8XhNEGEAhkJxTC6F8PYaooQqTcjbuzIuT8EFr08utDGHk-IsKgOg6c1QSG-3-vg5cGgGRNBIUGFoN8vhGE0LA2Kf0X4gyYcgxhbD6BwijgTWW04TJzlCCMGkzRNAOHpGZBqBFE5RgGNSHobxW7jDmnQnqKN0pvw2vI5RpYoacJglorwGhfYaH0YYx6jRjhNxwY8K69hkLSKRivRxr8kHoxQVjeA6id4wTDJ7Ju+C2TxmpsY-x9AvhmFqMhYhUiaF2KXg4ty8SFGJMYckhSnjq4nBZI0NOtRckZ2Vn-JO9Q+jXWiS5Lu-0xLMKSdtdiqiwDNKtiY8k9l2QmRCNpGwtgM6Z16SZaojM7p3SGZ3WJNT5GpXYVtcG7iKyzLKPMi+BhNDLMjCMOwNNlZXTODoywYRQwHPoavc0kBrn3AeZ4F4jNExrLOL7bpnswj9BMiYAxADfnVLFJcwgQLYLVB8X6R4tRfAPTMjnTw0CcEHz8KuX5usIBbxlmk+WyysI+BwXUMM9zHoOATl0BqGgHDJl+bASgRBID3mwLAfAAAjdAgLUlVytshaMSEbaWFKXZPJHQwhbi+L4W+hlbZxh+YHIAA */
  id: 'multiCartMachine',
  context: ({ input }: { input: MultiCartMachineInput }) => ({
    cartSummary: {
      carts: {},
      status: 'loading',
      error: undefined
    },
    cartItems: {},
    cartMembers: {},
    // Initialize with Redux values
    memberId: input.memberId,
  }),
  // Determine initial state - no always transitions at the top level to avoid recursion
  initial: 'checkingState',
  states: {
    checkingState: {
      always: [
        {
          target: 'initializing',
          guard: 'isUserLoggedIn'
        },
        {
          target: 'ready'
        }
      ]
    },
    initializing: {
      initial: 'loadingCartSummary',
      states: {
        loadingCartSummary: {
          invoke: {
            src: 'getMultiCarts',
            onDone: {
              target: 'loadingCartDetails',
              actions: [log(({ event }) => `assignCartSummary ${JSON.stringify(event.output)}`), 'assignCartSummary']
            },
            onError: {
              target: 'error',
              actions: ['logErrorEvent', 'assignCartSummaryFetchError']
            },
          }
        },
        loadingCartDetails: {
          type: 'parallel',
          onDone: {
            target: 'loaded'
          },
          states: {
            loadingCartItems: {
              initial: 'loading',
              states: {
                loading: {
                  invoke: {
                    src: 'getMultiCartItems',
                    input: ({ context }) => Object.keys(context.cartSummary),
                    onDone: {
                      target: 'done',
                      actions: 'assignCartItems'
                    },
                    onError: {
                      target: 'error',
                      actions: 'assignCartItemsFetchError'
                    },
                  }
                },
                done: {
                  type: 'final',
                },
                error: {
                  type: 'final',
                },
              }
            },
            loadingCartUsers: {
              initial: 'loading',
              states: {
                loading: {
                  invoke: {
                    src: 'getMultiCartMembers',
                    input: ({ context }) => Object.keys(context.cartSummary),
                    onDone: {
                      target: 'done',
                      actions: 'assignCartUsers'
                    },
                    onError: {
                      target: 'error',
                      actions: 'assignCartUsersFetchError'
                    },
                  }
                },
                done: {
                  type: 'final',
                },
                error: {
                  type: 'final',
                },
              }
            },
          }
        },
        loaded: {
          type: 'final'
        },
        error: {
          type: 'final',
        },
      },
      onDone: {
        target: 'ready',
      }
    },
    ready: {
      on: {
        'successful login': {
          target: 'checkingState',
        },
        'failed login': {
          target: 'ready',
        },
      }
    },
  },
})

export default multiCartMachine
